import {
  HttpClient,
  robotoHeaders,
  type PaginatedResponse,
} from "@/shared/http";
import { parseTimestampsAsBigInt } from "@/shared/json";

import { Association, type AssociationRecord } from "../association";

import {
  type MessagePathRecord,
  type MessagePathRepresentationMapping,
} from "./MessagePathRecord";
import { type TopicRecord, type TopicTimeBounds } from "./TopicRecord";

export class TopicService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getMessagePathRepresentations(
    topicId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<MessagePathRepresentationMapping[]> {
    const url = this.#httpClient.constructUrl(
      `v1/topics/id/${topicId}/message-path/representations`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<MessagePathRepresentationMapping[]>();
  }

  public async getTopicsByAssociation(
    association: AssociationRecord,
    options?: Partial<{
      abortSignal: AbortSignal;
      pageToken: string;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicRecord[]> {
    const assoc = new Association(association);
    const paginatedResponse = await this.getPagedTopicsByAssociation(
      assoc,
      options,
    );
    const topicRecords = [...paginatedResponse.items];

    let nextPageToken = paginatedResponse.next_token;
    while (nextPageToken) {
      const nextPaginatedResponse = await this.getPagedTopicsByAssociation(
        assoc,
        { ...options, pageToken: nextPageToken },
      );
      topicRecords.push(...nextPaginatedResponse.items);
      nextPageToken = nextPaginatedResponse.next_token;
    }

    return topicRecords;
  }

  /**
   * Gets all the topics for every file inside a given dataset.
   */
  public async getTopicsForDataset(
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicRecord[]> {
    const topicRecords: TopicRecord[] = [];
    let pageToken: string | undefined;

    do {
      const searchParams = new URLSearchParams();
      if (pageToken) {
        searchParams.append("page_token", pageToken);
      }

      const url = this.#httpClient.constructUrl(
        `v1/datasets/${datasetId}/topics`,
        searchParams,
      );

      const response = await this.#httpClient.get(url, {
        signal: options?.abortSignal,
        headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
      });

      const paginatedResponse = await response.json<
        PaginatedResponse<TopicRecord>
      >(parseTimestampsAsBigInt);

      topicRecords.push(...paginatedResponse.items);
      pageToken = paginatedResponse.next_token ?? undefined;
    } while (pageToken);

    return topicRecords;
  }

  /**
   * The time span every topic under `datasetId` covers, folded server-side.
   *
   * The cheap answer to "when does this dataset start?": one request against
   * an aggregate, rather than {@link getTopicsForDataset}'s full page-through
   * of every topic of every file just to read the smallest `start_time`.
   */
  public async getTimeBoundsForDataset(
    datasetId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicTimeBounds> {
    const url = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/topics/time-bounds`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TopicTimeBounds>(parseTimestampsAsBigInt);
  }

  /**
   * The time span every topic on `association` covers, folded server-side —
   * {@link getTimeBoundsForDataset}'s counterpart for a single file, and the
   * same saving over {@link getTopicsByAssociation}.
   */
  public async getTimeBoundsForAssociation(
    association: AssociationRecord,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicTimeBounds> {
    const encodedAssociation = new Association(association).urlEncode();
    const url = this.#httpClient.constructUrl(
      `v1/topics/association/${encodedAssociation}/time-bounds`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TopicTimeBounds>(parseTimestampsAsBigInt);
  }

  public async getTopicByNameAndAssociation(
    topicName: string,
    association: AssociationRecord,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<TopicRecord> {
    const encodedAssociation = new Association(association).urlEncode();
    const encodedTopicName = encodeURIComponent(topicName);
    const url = this.#httpClient.constructUrl(
      `v1/topics/association/${encodedAssociation}/name/${encodedTopicName}`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<TopicRecord>(parseTimestampsAsBigInt);
  }

  public async getTopicById(
    topicId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<TopicRecord> {
    const url = this.#httpClient.constructUrl(`v1/topics/id/${topicId}`);
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<TopicRecord>(parseTimestampsAsBigInt);
  }

  /**
   * Every distinct ``topic_name.message_path`` string in the org.
   *
   * The slow half of the org topic catalog: this is the endpoint that 504s for
   * the largest orgs (ENG-2818), and even when it answers it is capped
   * server-side at 10,000 entries, which is a fraction of the corpus for those
   * orgs. Kept as its own query so a caller that also wants topic names gets
   * them at their own pace rather than at this one's.
   */
  public async getMessagePathsForOrg(
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
      /**
       * Return only values starting with this, matched against the whole
       * `topic_name.message_path` string. Send what the user has typed:
       * without it the server's cap yields an alphabetical head of the corpus,
       * so for a large org anything sorting after the cut cannot be completed
       * to at all, however much the client filters.
       */
      prefix: string;
    }>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error(
        "getMessagePathsForOrg requires an org ID, none was provided",
      );
    }

    const params = new URLSearchParams();
    if (options.prefix) {
      params.set("prefix", options.prefix);
    }
    const requestUrl = this.#httpClient.constructUrl(
      "v1/topics/unique/topic_message_paths",
      params,
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });
    return response.json<string[]>();
  }

  /**
   * Every topic name in the org.
   *
   * The fast half of the org topic catalog — a plain index scan, ~240ms even
   * for the largest orgs — and a separate query from
   * {@link getMessagePathsForOrg} so callers take only what they need. The
   * skill editor wants names alone: its `[[topic]]` references resolve against
   * them, while a referenced topic's own fields come from
   * {@link getTopicFields}. Fetched together, the message-path corpus's
   * timeouts used to fail this one too.
   */
  public async getTopicNamesForOrg(
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("getTopicNamesForOrg requires an org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/topics/unique/topic_names",
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });
    return response.json<string[]>();
  }

  /**
   * Distinct field paths (dot-joined, e.g. `linear_acceleration.x`) of a single
   * topic, resolved by name through the shared-topics/fields schema. Scopes
   * field autocomplete to the referenced topic rather than the org-wide
   * message-path catalog. Returns `[]` for a topic that has no normalized
   * schema rows yet (org not enrolled in the migration, or topic predates it).
   */
  public async getTopicFields(
    topicName: string,
    options?: Partial<{
      abortSignal: AbortSignal;
      resourceOwnerId: string;
    }>,
  ): Promise<string[]> {
    if (!options?.resourceOwnerId) {
      throw Error("getTopicFields requires an org ID, none was provided");
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/topics/unique/topic_fields",
      new URLSearchParams({ topic_name: topicName }),
    );
    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options.resourceOwnerId }),
    });

    return response.json<string[]>();
  }

  private async getPagedTopicsByAssociation(
    association: Association,
    options?: Partial<{
      abortSignal: AbortSignal;
      pageToken: string;
      resourceOwnerId: string;
    }>,
  ): Promise<PaginatedResponse<TopicRecord>> {
    const encodedAssociation = association.urlEncode();
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.#httpClient.constructUrl(
      `v1/topics/association/${encodedAssociation}`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ resourceOwnerId: options?.resourceOwnerId }),
    });
    return response.json<PaginatedResponse<TopicRecord>>(
      parseTimestampsAsBigInt,
    );
  }

  public async getMessagePathById(
    messagePathId: string,
    options?: Partial<{
      abortSignal: AbortSignal;
    }>,
  ): Promise<MessagePathRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/topics/message-path/id/${messagePathId}`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<MessagePathRecord>();
  }
}
